import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from '@helpcenter/analytics-components/src/link';
import type { State } from '@helpcenter/common-state-selectors';
import { getThemeColor } from '@helpcenter/common-state-selectors/src/help-center';
import { isLoggedIn as isUserLoggedIn } from '@helpcenter/common-state-selectors/src/user';
import { isIsomorphicDompurifyLibUsed } from '@helpcenter/feature-flags';
import { sanitize } from 'dompurify';
import { sanitize as isomorphicSanitize } from 'isomorphic-dompurify';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import { usePathParam } from 'react-resource-router';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { withAnalyticsEvents } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import ShortcutIcon from '@atlaskit/icon/core/shortcut';
import { Box, Grid, xcss, Text } from '@atlaskit/primitives';
import { generateThemeBranding } from '@atlassian/csm-colour-theme-generator';
import { isCSMHelpCenter } from '@atlassian/help-center-common-util/advanced-help-center/advanced-help-center';
import {
	addSourceToUrl,
	is3pOrExternalSource,
} from '@atlassian/help-center-common-util/article-util';
import { getEnv } from '@atlassian/help-center-common-util/env';
import { urlRefsAndTarget } from '@atlassian/help-center-common-util/is-external-resource';
import { ArticleMetadata } from '../article-metadata';
import messages from '../articles/messages';
import { formatText, sendSearchUIAnalyticEvents, useVisibilityState } from '../common';
import { ResourceIcon } from '../resource-icon';
import type { articleItemFragmentV2$key } from './__generated__/articleItemFragmentV2.graphql';

export interface Props {
	isLoggedIn: boolean;
	itemOrder: number;
	term: string;
	result: articleItemFragmentV2$key;
	projectId?: number;
	themeColor: string;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
}

export const ArticleItem = ({
	itemOrder,
	result,
	term,
	themeColor,
	createAnalyticsEvent,
}: Props) => {
	const { formatMessage } = useIntl();
	const data = useFragment<articleItemFragmentV2$key>(
		graphql`
			fragment articleItemFragmentV2 on HelpObjectStoreArticleSearchResult
			@argumentDefinitions(isSourceSystemEnabled: { type: "Boolean!" }) {
				__typename
				ari
				title
				excerpt
				displayLink
				sourceSystem @include(if: $isSourceSystemEnabled)
				metadata {
					searchStrategy
				}
				...articleMetadataFragmentV2
			}
		`,
		result,
	);

	const [portalId] = usePathParam('portalId');
	const { title, excerpt, displayLink, metadata, ari, sourceSystem } = data;
	const { searchStrategy } = metadata;
	const isExternal = sourceSystem === 'EXTERNAL';
	const shortcutIcon = isExternal ? (
		<ShortcutIcon label={formatMessage(messages.shortcutIcon)} size="medium" spacing="compact" />
	) : null;
	const highlightedTitle = title ? formatText(title, term) : '';
	const highlightedDescription = excerpt ? formatText(excerpt, term) : '';

	const displayLinkWithSource = addSourceToUrl(
		displayLink,
		'search',
		is3pOrExternalSource(sourceSystem),
	);
	const linkProps = urlRefsAndTarget({
		displayLink: displayLinkWithSource,
		isAnExternalResource: is3pOrExternalSource(sourceSystem),
	});

	useEffect(() => {
		sendSearchUIAnalyticEvents(
			{
				sourceSystem,
				ari,
				portalId,
				searchAlgorithm: searchStrategy,
				action: 'list-item-rendered',
				resourceType: 'article',
			},
			createAnalyticsEvent,
			portalId,
		);
	}, [ari, createAnalyticsEvent, portalId, searchStrategy, sourceSystem]);

	const { isShortcutIconVisible, setIsHovered, setIsFocused } = useVisibilityState();

	const isJcsSearchExperienceEnabled = FeatureGates.checkGate('jcs_search_experience');
	const disableMetadata = isCSMHelpCenter(getEnv().helpCenterType) && isJcsSearchExperienceEnabled;

	return (
		<Box
			key={ari}
			xcss={containerStyles}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onFocusCapture={() => setIsFocused(true)}
			onBlurCapture={() => setIsFocused(false)}
			testId="search-page-result"
		>
			<Grid
				columnGap="space.150"
				templateAreas={[
					'icon heading header-right',
					'icon subheading-left header-right',
					'. body body',
				]}
				templateColumns="auto 1fr auto"
			>
				<Box xcss={iconAreaStyles}>
					<ResourceIcon
						displayLink={displayLink}
						entityType="ARTICLE"
						sourceSystem={sourceSystem}
						isExternal={isExternal}
						variant="tile"
					/>
				</Box>
				<Box xcss={headingAreaStyles}>
					<Link
						key={ari}
						actionSubjectId={isExternal ? 'external-article-link' : 'article-link'}
						data-test-id="result-link-container"
						aria-label={title}
						attributes={{
							ari,
							itemOrder,
							searchStrategy,
							sourceSystem,
							isEventFromPortal: !!portalId,
						}}
						{...linkProps}
					>
						<Text as="p">
							<span
								style={{
									color: isCSMHelpCenter(getEnv().helpCenterType)
										? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
											generateThemeBranding(themeColor).color900
										: themeColor,
								}}
								dangerouslySetInnerHTML={{
									__html: isIsomorphicDompurifyLibUsed()
										? isomorphicSanitize(highlightedTitle)
										: sanitize(highlightedTitle),
								}}
							/>
						</Text>
					</Link>
				</Box>
				<Box
					xcss={[subheadingLeftAreaStyles, subheadingStyles]}
					testId="search-page-result-metadata"
				>
					{!disableMetadata && <ArticleMetadata result={data} />}
				</Box>
				<Box
					xcss={
						isShortcutIconVisible ? headerRightAreaStylesVisible : headerRightAreaStylesInVisible
					}
					testId="search-result-shortcut-icon"
				>
					{shortcutIcon}
				</Box>
				<Box xcss={disableMetadata ? jcsBodyAreaStyles : bodyAreaStyles}>
					<Text>
						<p
							dangerouslySetInnerHTML={{
								__html: isIsomorphicDompurifyLibUsed()
									? isomorphicSanitize(highlightedDescription)
									: sanitize(highlightedDescription),
							}}
						/>
					</Text>
				</Box>
			</Grid>
		</Box>
	);
};

const ConnectedArticleItem = connect((state: State) => {
	return {
		isLoggedIn: isUserLoggedIn(state),
		themeColor: getThemeColor(state),
	};
})(ArticleItem);

export default withAnalyticsEvents()(ConnectedArticleItem);

const containerStyles = xcss({
	padding: 'space.100',
	marginLeft: 'space.negative.100',
	marginRight: 'space.negative.100',
	borderRadius: 'radius.small',
});
const iconAreaStyles = xcss({
	gridArea: 'icon',
	width: 'size.300',
	height: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'right',
	textDecoration: 'none',
});
const headingAreaStyles = xcss({
	gridArea: 'heading',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '20px',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	font: 'font.body.large',
});
const subheadingStyles = xcss({
	color: 'color.text.subtlest',
	marginTop: 'space.050',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '12px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '16px',
	overflow: 'clip',
	overflowClipMargin: '5px',
});
const subheadingLeftAreaStyles = xcss({
	gridArea: 'subheading-left',
	display: 'flex',
	alignItems: 'center',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '16px',
});
const headerRightAreaStylesInVisible = xcss({
	gridArea: 'header-right',
	display: 'flex',
	opacity: 0,
	alignItems: 'flex-start',
	justifyContent: 'flex-end',
});
const headerRightAreaStylesVisible = xcss({
	gridArea: 'header-right',
	display: 'flex',
	opacity: 1,
	alignItems: 'flex-start',
	justifyContent: 'flex-end',
});
const bodyAreaStyles = xcss({
	gridArea: 'body',
	marginTop: 'space.100',
	overflowWrap: 'break-word',
});
const jcsBodyAreaStyles = xcss({
	gridArea: 'body',
	marginTop: 'space.0',
	overflowWrap: 'break-word',
});
